import { useMemo, useState, useCallback } from 'react';
import { Compare, ChevronDown, ChevronUp } from '@carbon/react/icons';
import { ColumnDef, Row } from '@tanstack/react-table';
import { FloatingTooltip } from 'components/floating-tooltip';
import { LinkWithHover } from 'components/hover-card-resource';
import { TanStackTable, GroupByOption } from './ownership-tanstack-table';
import tableStyles from './ownership-tanstack-table.module.scss';
import {
  OwnershipGraph,
  OwnershipNode,
  InterestEncumbrance,
  DocumentRecord,
  getDocumentForEdge,
} from './types';
import {
  getNodeNRI,
  buildNriFormula,
  buildHeldEncumbrances,
  formatDepthBand,
  HeldEncumbrance,
} from './data-helpers';
import { formatNumber } from 'utils/number';
import {
  OwnershipTableConfig,
  DEFAULT_OWNERSHIP_TABLE_CONFIG,
} from './ownership-table-settings';
import { useSetHoveredNodeIdThrottled } from './hover-state';

// ============================================================================
// Props
// ============================================================================

export type OwnershipGroupBy = 'none' | 'entity' | 'property';

export interface AssetClickData {
  propertyName: string;
  nodeId: string;
  anchorRect?: { top: number; left: number; bottom: number; right: number };
  /** true = pin the popover (click); false/undefined = hover preview */
  pin?: boolean;
}

// Encumbrance badge — same icon as the GoJS graph nodes (dollar+arrow+document).
// Burdened = red, icon rotated 180°. Encumbering = orange, icon upright.
function EncumbranceBadge({ type }: { type: 'burdened' | 'encumbering' }) {
  const color = type === 'burdened' ? '#da1e28' : '#ff832b';
  const rotation = type === 'burdened' ? 'rotate(180 16 16)' : '';
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
      <circle
        cx="9"
        cy="9"
        r="8.5"
        fill={color}
        stroke="white"
        strokeWidth="1"
      />
      <g transform={`translate(3, 3) scale(0.375) ${rotation}`}>
        <path
          d="M5 26V15.828l-3.586 3.586L0 18l6-6 6 6-1.414 1.414L7 15.828V26h12v2H7a2 2 0 01-2-2zm26-8h-3v-2h-2v2h-1a2 2 0 00-2 2v2a2 2 0 002 2h4v2h-6v2h3v2h2v-2h1a2 2 0 002-2v-2a2 2 0 00-2-2h-4v-2h6v-2zM28 12V6a2 2 0 00-2-2H12v2h14v6M2 6h5v2H2zm0-4h8v2H2z"
          fill="white"
        />
      </g>
    </svg>
  );
}

interface OwnershipTanstackTableProps {
  ownershipGraph: OwnershipGraph;
  /** Full (unfiltered) graph used for held-encumbrance lookup */
  fullGraph?: OwnershipGraph;
  documents: Record<string, DocumentRecord>;
  selectedNodeId?: string | null;
  onNodeRowClick?: (nodeId: string) => void;
  onAssetClick?: (data: AssetClickData) => void;
  onAssetLeave?: () => void;
  tableConfig?: OwnershipTableConfig;
  groupBy?: OwnershipGroupBy;
  onGroupByChange?: (groupBy: OwnershipGroupBy) => void;
  /** When 'changes', show only entities that changed vs beforeGraph */
  diffMode?: 'all' | 'changes';
  onDiffModeChange?: (mode: 'all' | 'changes') => void;
  /** Ownership graph before the current document — used for diff */
  beforeGraph?: OwnershipGraph | null;
}

const OWNERSHIP_GROUP_OPTIONS: GroupByOption[] = [
  { id: 'none', text: 'No Grouping' },
  { id: 'entity', text: 'Group by Entity' },
  { id: 'property', text: 'Group by Property' },
];

// ============================================================================
// Row shape — one flat object per leaf node
// ============================================================================

interface DirectEncumbrance {
  interest_type: string;
  enc: InterestEncumbrance;
  ownerNode: OwnershipNode | null;
}

export interface OwnershipRow {
  nodeId: string;
  entityId: number;
  entityName: string;
  effectiveDate: string | null;
  propertyName: string;
  propertyType: string;
  depthRange: string;
  // interest fractions stored as 0–1 numbers; 0 means not held
  mineral: number;
  surface: number;
  executive: number;
  royalty: number;
  bonus: number;
  delay: number;
  working: number;
  orri: number;
  nri: number | null;
  nriFormula: string | null;
  // Encumbrances
  directEncumbrances: DirectEncumbrance[];
  heldEncumbrances: HeldEncumbrance[];
  hasEncumbrances: boolean;
  // Diff mode — change indicator and per-field deltas
  changeType?: 'added' | 'removed' | 'changed' | 'unchanged';
  deltas?: Record<string, number>;
  // Grouping support
  isGroupHeader?: boolean;
  groupLabel?: string;
  groupCount?: number;
  subRows?: OwnershipRow[];
}

// ============================================================================
// Helpers
// ============================================================================

function getInterestF(node: OwnershipNode, type: string): number {
  return node.interests.find((i) => i.interestType === type)?.fractionF ?? 0;
}

function fmtDecimal(v: number, dp: number): string {
  return v === 0 ? '\u2014' : formatNumber(v, dp, dp, 'decimal', false);
}

export function buildRows(
  graph: OwnershipGraph,
  documents: Record<string, DocumentRecord>,
  fullGraph?: OwnershipGraph
): OwnershipRow[] {
  const getEffectiveDate = (nodeId: string): string | null => {
    let latest = '';
    for (const edge of Object.values(graph.edges)) {
      if (String(edge.toNodeId) === nodeId) {
        const effectiveDate =
          getDocumentForEdge(edge, documents)?.effectiveDate ?? null;
        if (effectiveDate && effectiveDate > latest) latest = effectiveDate;
      }
    }
    return latest || null;
  };

  return Object.values(graph.nodes)
    .filter((n) => {
      const key = String(n.id);
      const isLeaf = !graph.adjacency[key] || graph.adjacency[key].length === 0;
      return isLeaf && n.interests.some((i) => i.fractionF > 0);
    })
    .map((n) => {
      const depthRange = formatDepthBand(n.property.volume.depthRange);

      const nodeIdStr = String(n.id);
      const directEncs: DirectEncumbrance[] = n.interests.flatMap((i) =>
        (i.encumbrances ?? []).map((enc) => ({
          interest_type: i.interestType,
          enc,
          ownerNode: enc.encumbranceOwnerNodeId
            ? ((fullGraph ?? graph).nodes[String(enc.encumbranceOwnerNodeId)] ??
              null)
            : null,
        }))
      );
      const heldEncs = buildHeldEncumbrances(nodeIdStr, fullGraph ?? graph);

      return {
        nodeId: nodeIdStr,
        entityId: n.entity.entityId,
        entityName: n.entity.entityName,
        effectiveDate: getEffectiveDate(nodeIdStr),
        propertyName: n.property.propertyName,
        propertyType: n.property.propertyType ?? 'tract',
        depthRange,
        mineral: getInterestF(n, 'mineral'),
        surface: getInterestF(n, 'surface'),
        executive: getInterestF(n, 'executive'),
        royalty: getInterestF(n, 'royalty'),
        bonus: getInterestF(n, 'bonus'),
        delay: getInterestF(n, 'delay'),
        working: getInterestF(n, 'working'),
        orri: getInterestF(n, 'orri'),
        nri: getNodeNRI(n),
        nriFormula: buildNriFormula(n),
        directEncumbrances: directEncs,
        heldEncumbrances: heldEncs,
        hasEncumbrances: directEncs.length > 0 || heldEncs.length > 0,
      };
    });
}

const INTEREST_KEYS = [
  'mineral',
  'surface',
  'executive',
  'royalty',
  'bonus',
  'delay',
  'working',
  'orri',
] as const;

export function buildDiffRows(
  afterGraph: OwnershipGraph,
  beforeGraph: OwnershipGraph,
  documents: Record<string, DocumentRecord>
): OwnershipRow[] {
  const afterRows = buildRows(afterGraph, documents);
  const beforeRows = buildRows(beforeGraph, documents);

  // Key by entity_id + property_name for matching
  const beforeByKey = new Map<string, OwnershipRow>();
  for (const row of beforeRows) {
    beforeByKey.set(`${row.entityId}::${row.propertyName}`, row);
  }

  const afterByKey = new Map<string, OwnershipRow>();
  for (const row of afterRows) {
    afterByKey.set(`${row.entityId}::${row.propertyName}`, row);
  }

  const result: OwnershipRow[] = [];
  const allKeys = new Set([...beforeByKey.keys(), ...afterByKey.keys()]);

  for (const key of allKeys) {
    const before = beforeByKey.get(key);
    const after = afterByKey.get(key);

    // Compute per-field deltas
    const computeDeltas = (
      a: OwnershipRow | undefined,
      b: OwnershipRow | undefined
    ): Record<string, number> => {
      const d: Record<string, number> = {};
      for (const k of INTEREST_KEYS) {
        const av = (a?.[k] as number) ?? 0;
        const bv = (b?.[k] as number) ?? 0;
        const delta = av - bv;
        if (Math.abs(delta) > 1e-10) d[k] = delta;
      }
      return d;
    };

    if (after && !before) {
      const deltas = computeDeltas(after, undefined);
      result.push({ ...after, changeType: 'added', deltas });
    } else if (before && !after) {
      // For removed: show the before values with negative deltas
      const deltas: Record<string, number> = {};
      for (const k of INTEREST_KEYS) {
        const v = (before[k] as number) ?? 0;
        if (v > 1e-10) deltas[k] = -v;
      }
      result.push({ ...before, changeType: 'removed', deltas });
    } else if (before && after) {
      const deltas = computeDeltas(after, before);
      const changed = Object.keys(deltas).length > 0;
      if (changed) {
        result.push({ ...after, changeType: 'changed', deltas });
      }
      // Skip unchanged rows in diff mode
    }
  }

  // Sort: added first, then changed, then removed
  const order: Record<string, number> = {
    added: 0,
    changed: 1,
    removed: 2,
    unchanged: 3,
  };
  result.sort(
    (a, b) => (order[a.changeType!] ?? 3) - (order[b.changeType!] ?? 3)
  );

  return result;
}

export function groupRows(
  rows: OwnershipRow[],
  groupBy: OwnershipGroupBy
): OwnershipRow[] {
  if (groupBy === 'none') return rows;

  const groups = new Map<string, OwnershipRow[]>();
  for (const row of rows) {
    const key = groupBy === 'entity' ? row.entityName : row.propertyName;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  const sorted = Array.from(groups.entries()).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const showTotals = groupBy === 'property';

  return sorted.map(([key, children]) => {
    const sum = (field: keyof OwnershipRow) => {
      return showTotals
        ? children.reduce((acc, r) => acc + ((r[field] as number) || 0), 0)
        : 0;
    };
    const nriValues = children
      .map((r) => r.nri)
      .filter((v): v is number => v !== null);
    return {
      nodeId: `group-${key}`,
      entityId: children[0].entityId,
      entityName: groupBy === 'entity' ? key : '',
      effectiveDate: null,
      propertyName: groupBy === 'property' ? key : '',
      propertyType: children[0].propertyType,
      depthRange: '',
      mineral: sum('mineral'),
      surface: sum('surface'),
      executive: sum('executive'),
      royalty: sum('royalty'),
      bonus: sum('bonus'),
      delay: sum('delay'),
      working: sum('working'),
      orri: sum('orri'),
      nri:
        showTotals && nriValues.length > 0
          ? nriValues.reduce((a, b) => a + b, 0)
          : null,
      nriFormula: null,
      directEncumbrances: [],
      heldEncumbrances: [],
      hasEncumbrances: false,
      isGroupHeader: true,
      groupLabel: key,
      groupCount: children.length,
      subRows: children,
    };
  });
}

function buildColumns(
  dp: number,
  onAssetClick?: (data: AssetClickData) => void,
  onAssetLeave?: () => void,
  groupBy: OwnershipGroupBy = 'none',
  expandedRows?: Set<string>,
  onToggleExpand?: (nodeId: string) => void
): ColumnDef<OwnershipRow>[] {
  const interestCol = (
    accessorKey: keyof OwnershipRow,
    header: string,
    size = 135
  ): ColumnDef<OwnershipRow> => ({
    accessorKey,
    header,
    size,
    meta: { filterVariant: 'range' as const },
    cell: ({ getValue, row }) => {
      const v = getValue() as number;
      if (row.original.isGroupHeader && groupBy !== 'property') return null;
      const text = fmtDecimal(v, dp);
      if (row.original.isGroupHeader) {
        return <span style={{ fontWeight: 600 }}>{text}</span>;
      }
      const delta = row.original.deltas?.[accessorKey as string];
      if (delta !== undefined && Math.abs(delta) > 1e-10) {
        const sign = delta > 0 ? '+' : '\u2212';
        const color = delta > 0 ? '#198038' : '#da1e28';
        return (
          <span>
            {text}{' '}
            <span style={{ color, fontSize: '0.6875rem', marginLeft: 3 }}>
              {sign}
              {formatNumber(Math.abs(delta), dp, dp, 'decimal', false)}
            </span>
          </span>
        );
      }
      return text;
    },
  });

  const groupHeaderCell = ({ row }: { row: Row<OwnershipRow> }) => {
    const { isGroupHeader, groupLabel } = row.original;
    if (!isGroupHeader) return null;
    return <span style={{ fontWeight: 600 }}>{groupLabel}</span>;
  };

  const entityCol: ColumnDef<OwnershipRow> = {
    accessorKey: 'entityName',
    header: 'Entity',
    size: 240,
    minSize: 80,
    cell: ({ row }) => {
      const { isGroupHeader } = row.original;
      if (isGroupHeader) {
        return groupBy !== 'property' ? groupHeaderCell({ row }) : null;
      }
      const { entityName, effectiveDate, entityId } = row.original;
      const dateDisplay = effectiveDate
        ? new Date(effectiveDate + 'T00:00:00').toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })
        : null;
      const ct = row.original.changeType;
      const { hasEncumbrances, nodeId } = row.original;
      const isExpanded = expandedRows?.has(nodeId) ?? false;
      return (
        <div
          title={entityName}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}
        >
          {hasEncumbrances && onToggleExpand && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(nodeId);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                padding: 0,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#8a3ffc',
                flexShrink: 0,
                marginTop: 2,
              }}
              title={isExpanded ? 'Hide encumbrances' : 'Show encumbrances'}
            >
              {isExpanded ? <ChevronUp /> : <ChevronDown />}
            </button>
          )}
          {ct === 'added' && (
            <span
              style={{
                color: '#198038',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              +
            </span>
          )}
          {ct === 'removed' && (
            <span
              style={{
                color: '#da1e28',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {'\u2212'}
            </span>
          )}
          {ct === 'changed' && (
            <span
              style={{
                color: '#ff832b',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {'\u0394'}
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <LinkWithHover kind="entity" href={`/entities/${entityId}`}>
              <a
                href={`/entities/${entityId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {entityName}
              </a>
            </LinkWithHover>
            {dateDisplay && (
              <div
                style={{
                  fontSize: '0.6875rem',
                  color: '#6f6f6f',
                  marginTop: 2,
                }}
              >
                Eff. {dateDisplay}
              </div>
            )}
            {isExpanded && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: '0.6875rem',
                  color: '#525252',
                  lineHeight: 1.5,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {row.original.directEncumbrances.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: '#525252',
                        marginBottom: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <EncumbranceBadge type="burdened" />
                      Burdened by:
                    </div>
                    {row.original.directEncumbrances.map((de, i) => (
                      <div
                        key={i}
                        style={{
                          color: '#6f6f6f',
                          padding: '1px 0',
                          paddingLeft: 20,
                        }}
                      >
                        {de.enc.type}{' '}
                        {de.ownerNode ? (
                          <a
                            href={`/entities/${de.ownerNode.entity.entityId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#0043ce', textDecoration: 'none' }}
                          >
                            {de.ownerNode.entity.entityName}
                          </a>
                        ) : null}{' '}
                        {de.interest_type} {String(de.enc.fraction ?? '')}
                      </div>
                    ))}
                  </div>
                )}
                {row.original.heldEncumbrances.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        color: '#525252',
                        marginBottom: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <EncumbranceBadge type="encumbering" />
                      Holds burden on:
                    </div>
                    {row.original.heldEncumbrances.map((he, i) => (
                      <div
                        key={i}
                        style={{
                          color: '#6f6f6f',
                          padding: '1px 0',
                          paddingLeft: 20,
                        }}
                      >
                        {he.enc.type}{' '}
                        <a
                          href={`/entities/${he.burdened_node.entity.entityId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#0043ce', textDecoration: 'none' }}
                        >
                          {he.burdened_node.entity.entityName}
                        </a>{' '}
                        on {he.interest_type} @{' '}
                        {he.burdened_node.property.propertyName}{' '}
                        {String(he.enc.fraction ?? '')}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
  };

  const propertyCol: ColumnDef<OwnershipRow> = {
    accessorKey: 'propertyName',
    header: 'Property',
    size: 290,
    minSize: 100,
    cell: ({ row }) => {
      if (row.original.isGroupHeader) {
        // When property is the lead column (groupBy === 'property'), show group header here
        return groupBy === 'property' ? groupHeaderCell({ row }) : null;
      }
      const { propertyName, propertyType, depthRange, nodeId } = row.original;
      const typeLabel =
        propertyType === 'tract'
          ? 'Tract'
          : propertyType === 'tract_volume'
            ? 'Tract Volume'
            : 'Well';
      return (
        <div>
          {onAssetClick ? (
            <span
              className={tableStyles['property-link']}
              onMouseEnter={(e) => {
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                onAssetClick({ propertyName, nodeId, anchorRect: rect });
              }}
              onMouseLeave={onAssetLeave}
              onClick={(e) => {
                e.stopPropagation();
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                onAssetClick({
                  propertyName,
                  nodeId,
                  anchorRect: rect,
                  pin: true,
                });
              }}
            >
              {propertyName}
            </span>
          ) : (
            <div style={{ fontSize: '0.875rem' }}>{propertyName}</div>
          )}
          <div
            style={{
              fontSize: '0.6875rem',
              color: '#6f6f6f',
              marginTop: 2,
              display: 'flex',
              gap: 4,
              alignItems: 'center',
            }}
          >
            <span
              style={{
                background: '#f4f4f4',
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                padding: '0 4px',
                fontSize: '0.6875rem',
              }}
            >
              {typeLabel}
            </span>
            <span>{depthRange}</span>
          </div>
        </div>
      );
    },
  };

  // Swap entity/property column order when grouping by property
  const leadCols: ColumnDef<OwnershipRow>[] =
    groupBy === 'property'
      ? [propertyCol, entityCol]
      : [entityCol, propertyCol];

  return [
    ...leadCols,
    {
      accessorKey: 'nri',
      header: 'NRI',
      size: 135,
      meta: { filterVariant: 'range' as const },
      cell: ({ getValue, row }) => {
        if (row.original.isGroupHeader && groupBy !== 'property') return null;
        const v = getValue() as number | null;
        const text = v === null ? '\u2014' : fmtDecimal(v, dp);
        if (row.original.isGroupHeader) {
          return <span style={{ fontWeight: 600 }}>{text}</span>;
        }
        const { nriFormula } = row.original;
        if (nriFormula) {
          return (
            <FloatingTooltip
              content={
                <span style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
                  {nriFormula}
                </span>
              }
              placement="top"
            >
              {text}
            </FloatingTooltip>
          );
        }
        return text;
      },
    },
    interestCol('mineral', 'Mineral'),
    interestCol('surface', 'Surface'),
    interestCol('executive', 'Exec'),
    interestCol('royalty', 'Royalty'),
    interestCol('bonus', 'Bonus'),
    interestCol('delay', 'Delay'),
    interestCol('working', 'WI'),
    interestCol('orri', 'ORRI'),
  ];
}

// ============================================================================
// Component
// ============================================================================

export function OwnershipTanstackTable({
  ownershipGraph,
  fullGraph,
  documents,
  selectedNodeId,
  onNodeRowClick,
  onAssetClick,
  onAssetLeave,
  tableConfig = DEFAULT_OWNERSHIP_TABLE_CONFIG,
  groupBy = 'none',
  onGroupByChange,
  diffMode = 'all',
  onDiffModeChange,
  beforeGraph,
}: OwnershipTanstackTableProps) {
  const { decimalPlaces } = tableConfig;

  const setHoveredNodeId = useSetHoveredNodeIdThrottled();
  const handleRowHover = useCallback(
    (row: OwnershipRow | null) => {
      if (!row) {
        setHoveredNodeId(null);
        return;
      }
      if (row.isGroupHeader) {
        // When grouped by property, every child shares the same property so
        // highlighting the first child's geometry matches the group. Entity
        // groups span multiple properties — no single highlight applies.
        if (groupBy === 'property' && row.subRows && row.subRows.length > 0) {
          setHoveredNodeId(row.subRows[0].nodeId);
        } else {
          setHoveredNodeId(null);
        }
        return;
      }
      setHoveredNodeId(row.nodeId);
    },
    [setHoveredNodeId, groupBy]
  );

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleToggleExpand = useCallback((nodeId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const flatRows = useMemo(() => {
    if (diffMode === 'changes') {
      if (beforeGraph) {
        return buildDiffRows(ownershipGraph, beforeGraph, documents);
      }
      // No "before" graph (first document) — everything is "added"
      return buildRows(ownershipGraph, documents, fullGraph).map((row) => {
        const deltas: Record<string, number> = {};
        for (const k of INTEREST_KEYS) {
          const v = (row[k] as number) ?? 0;
          if (v > 1e-10) deltas[k] = v;
        }
        return { ...row, changeType: 'added' as const, deltas };
      });
    }
    return buildRows(ownershipGraph, documents, fullGraph);
  }, [ownershipGraph, fullGraph, documents, diffMode, beforeGraph]);

  const rows = useMemo(() => groupRows(flatRows, groupBy), [flatRows, groupBy]);

  const columns = useMemo(
    () =>
      buildColumns(
        decimalPlaces,
        onAssetClick,
        onAssetLeave,
        groupBy,
        expandedRows,
        handleToggleExpand
      ),
    [
      decimalPlaces,
      onAssetClick,
      onAssetLeave,
      groupBy,
      expandedRows,
      handleToggleExpand,
    ]
  );

  const isGrouped = groupBy !== 'none';

  // Force re-mount when the graph or grouping changes so TanStack picks up new rows
  const stableKey = `${Object.keys(ownershipGraph.nodes).length}-${
    Object.keys(ownershipGraph.edges).length
  }-${groupBy}`;

  const handleRowClick = useMemo(() => {
    if (!onNodeRowClick) return undefined;
    return (row: OwnershipRow) => {
      if (row.isGroupHeader) return; // Don't fire click for group headers
      onNodeRowClick(row.nodeId);
    };
  }, [onNodeRowClick]);

  return (
    <TanStackTable<OwnershipRow>
      key={stableKey}
      data={rows}
      columns={columns}
      enableExpanding={isGrouped}
      searchPlaceholder="Search ownership nodes..."
      selectedRowId={selectedNodeId ?? undefined}
      onRowClick={handleRowClick}
      getRowId={(row) => row.nodeId}
      groupByOptions={onGroupByChange ? OWNERSHIP_GROUP_OPTIONS : undefined}
      groupBy={groupBy}
      onGroupByChange={
        onGroupByChange
          ? (id) => onGroupByChange(id as OwnershipGroupBy)
          : undefined
      }
      toolbarExtra={
        onDiffModeChange ? (
          <FloatingTooltip
            content={
              diffMode === 'changes'
                ? 'Show all ownership'
                : 'Show changes only'
            }
            placement="bottom"
          >
            <button
              className={`${tableStyles['toolbar-btn']} ${
                diffMode === 'changes' ? tableStyles['toolbar-btn--active'] : ''
              }`}
              onClick={() =>
                onDiffModeChange(diffMode === 'changes' ? 'all' : 'changes')
              }
            >
              <Compare />
            </button>
          </FloatingTooltip>
        ) : undefined
      }
      getRowClassName={
        diffMode === 'changes'
          ? (row) => {
              if (row.changeType === 'added')
                return tableStyles['diff-row-added'];
              if (row.changeType === 'removed')
                return tableStyles['diff-row-removed'];
              if (row.changeType === 'changed')
                return tableStyles['diff-row-changed'];
              return undefined;
            }
          : undefined
      }
      onRowHover={handleRowHover}
    />
  );
}
